import { createTheme } from '@mui/material/styles';
import typography from './typography';

import vars from '../assets/core/variables-mui';

const MuiTheme = createTheme({
  palette: {
    // Primary palette - Deep Navy
    primary: {
      light: vars.primaryLight,
      main: vars.primary,
      dark: vars.primaryDark,
      contrastText: '#ffffff'
    },
    // Secondary palette - Amber
    secondary: {
      light: vars.secondaryLight,
      main: vars.secondary,
      dark: vars.secondaryDark,
      contrastText: '#000000'
    },
    // Semantic color palettes
    error: {
      light: vars.errorLight,
      main: vars.error,
      dark: vars.errorDark,
      contrastText: '#ffffff'
    },
    warning: {
      light: vars.warningLight,
      main: vars.warning,
      dark: vars.warningDark,
      contrastText: '#000000'
    },
    info: {
      light: vars.infoLight,
      main: vars.info,
      dark: vars.infoDark,
      contrastText: '#ffffff'
    },
    success: {
      light: vars.successLight,
      main: vars.success,
      dark: vars.successDark,
      contrastText: '#ffffff'
    },
    // Grey palette for neutral elements
    grey: {
      50: vars.grey50,
      100: vars.grey100,
      200: vars.grey200,
      300: vars.grey300,
      400: vars.grey400,
      500: vars.grey500,
      600: vars.grey600,
      700: vars.grey700,
      800: vars.grey800,
      900: vars.grey900,
      A100: vars.grey100,
      A200: vars.grey300,
      A400: vars.grey600,
      A700: vars.grey700
    },
    // Background colors
    background: {
      default: vars.grey50,
      paper: '#ffffff'
    },
    // Text colors
    text: {
      primary: vars.grey900,
      secondary: vars.grey600,
      disabled: vars.grey400
    },
    // Divider color
    divider: vars.grey300,
    // Action colors
    action: {
      active: vars.primary,
      hover: 'rgba(60, 60, 60, 0.04)', // primary with low opacity
      selected: 'rgba(60, 60, 60, 0.08)',
      disabled: vars.grey400,
      disabledBackground: vars.grey200
    },
    // Custom utility colors for specific use cases
    utilities: {
      // Status colors for chips and tags
      active: {
        main: vars.success,
        light: 'rgba(76, 175, 80, 0.1)',
        contrastText: vars.success
      },
      inactive: {
        main: vars.warning,
        light: 'rgba(255, 152, 0, 0.1)',
        contrastText: vars.warning
      },
      alert: {
        main: vars.error,
        light: 'rgba(244, 67, 54, 0.1)',
        contrastText: vars.error
      },
      tenant: {
        main: vars.info,
        light: 'rgba(33, 150, 243, 0.1)',
        contrastText: vars.info
      },
      neutral: {
        main: vars.grey500,
        light: 'rgba(158, 158, 158, 0.1)',
        contrastText: vars.grey700
      }
    },
    contrastThreshold: 3,
    tonalOffset: 0.2
  },
  shape: {
    borderRadius: '0.5rem'
  },
  components: {
    // Button component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '8px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(60, 60, 60, 0.2)'
          }
        },
        text: {
          paddingLeft: '16px',
          paddingRight: '16px'
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 8px rgba(60, 60, 60, 0.3)'
          }
        },
        containedPrimary: {
          backgroundColor: vars.primary,
          '&:hover': {
            backgroundColor: vars.primaryDark
          }
        },
        containedSecondary: {
          backgroundColor: vars.secondary,
          color: '#000000',
          '&:hover': {
            backgroundColor: vars.secondaryDark
          }
        }
      }
    },
    // Chip component overrides for consistent styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 500,
          fontSize: '0.875rem'
        },
        outlined: {
          borderWidth: '1px'
        }
      }
    },
    // Card component overrides
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }
      }
    },
    // Tooltip component overrides
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: vars.grey800,
          padding: '8px 16px',
          fontSize: '13px',
          borderRadius: '8px'
        },
        arrow: {
          color: vars.grey800
        }
      }
    },
    // Table component overrides
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(60, 60, 60, 0.04)'
          }
        }
      }
    },
    // Paper component overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px'
        }
      }
    },
    // ButtonGroup component overrides
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: 'none',
          '& .MuiButton-root': {
            borderColor: vars.primary,
            color: vars.primary,
            '&:hover': {
              backgroundColor: 'rgba(0, 51, 102, 0.04)',
              borderColor: vars.primaryDark
            }
          },
          '& .MuiButton-root.active': {
            backgroundColor: vars.primary,
            color: '#ffffff',
            borderColor: vars.primary,
            '&:hover': {
              backgroundColor: vars.primaryDark,
              borderColor: vars.primaryDark
            }
          }
        },
        outlined: {
          '& .MuiButton-root': {
            borderWidth: '1px',
            '&:not(:last-child)': {
              borderRightColor: vars.primary
            }
          }
        }
      }
    },
    // ToggleButtonGroup component overrides
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& .MuiToggleButton-root': {
            border: `1px solid ${vars.primary}`,
            color: vars.primary,
            '&:hover': {
              backgroundColor: 'rgba(0, 51, 102, 0.04)'
            },
            '&.Mui-selected': {
              backgroundColor: vars.primary,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: vars.primaryDark
              }
            }
          }
        }
      }
    },
    // AppBar component overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: vars.primary,
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 51, 102, 0.15)',
          '&.MuiAppBar-colorSecondary': {
            backgroundColor: vars.primary,
            color: '#ffffff'
          },
          '&.MuiAppBar-colorPrimary': {
            backgroundColor: vars.primary,
            color: '#ffffff'
          }
        }
      }
    },
    // Toolbar component overrides
    MuiToolbar: {
      styleOverrides: {
        root: {
          '& .MuiIconButton-root': {
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }
          },
          '& .MuiTypography-root': {
            color: '#ffffff'
          }
        }
      }
    },
    // Tabs component overrides for AppBar
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.9)'
            },
            '&.Mui-selected': {
              color: '#ffffff',
              fontWeight: 600
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: vars.secondary,
            height: 3
          }
        }
      }
    },
    // Individual Tab component overrides
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          '&.Mui-selected': {
            color: '#ffffff'
          }
        }
      }
    },
    // TablePagination component overrides
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: vars.grey900,
          '& .MuiIconButton-root': {
            color: vars.grey900,
            '&.Mui-disabled': {
              color: vars.grey400
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: vars.grey900
          }
        }
      }
    }
  },
  typography
});

export default MuiTheme;
